if (!customElements.get('installment-plans')) {
  class InstallmentPlans extends HTMLElement {
    constructor() {
      super();
      this.dropdown = this.querySelector('[data-installment-dropdown]');
      this.toggleButton = this.querySelector('[data-installment-toggle]');
      this.baseVariantPrice = Number(this.dataset.currentPrice || 0);
      this.lastAppliedPrice = this.baseVariantPrice;
      this.productRoot = this.closest('.product-info, .product, [data-product-root], section') || this.parentElement;
      this.syncTimer = null;

      this.groupDuplicateProviders();

      this.toggleButton?.addEventListener('click', () => this.toggleDropdown());
      this.querySelectorAll('[data-installment-provider-open]').forEach((button) => {
        button.addEventListener('click', () => this.openProviderModal(button.dataset.installmentProviderOpen));
      });
      this.querySelectorAll('[data-installment-provider-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeProviderModal(button.closest('[data-installment-provider-modal]')));
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeAllModals();
      });

      this.boundVariantChange = (event) => this.handleVariantChange(event);
      document.addEventListener('variant:change', this.boundVariantChange);
      document.addEventListener('product:variant-change', this.boundVariantChange);
      this.observeExternalDiscountPrice();
      [60, 250, 600, 1200, 2200].forEach((delay) => window.setTimeout(() => this.syncFromRenderedPrice(), delay));
      this.updateOptions(this.baseVariantPrice);
    }


    groupDuplicateProviders() {
      const buttons = Array.from(this.querySelectorAll('[data-installment-provider-open]'));
      if (!buttons.length) return;

      const groups = new Map();

      buttons.forEach((button) => {
        const nameEl = button.querySelector('.installment-plans__provider-logo-name');
        const rawName = (nameEl?.textContent || button.getAttribute('aria-label') || '').trim();
        const key = rawName
          .toLowerCase()
          .replace(/bank/g, '')
          .replace(/[^a-z0-9]+/g, '')
          .trim();

        if (!key) return;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(button);
      });

      groups.forEach((groupButtons) => {
        if (groupButtons.length <= 1) return;

        const primaryButton = groupButtons[0];
        const primaryModalId = primaryButton.dataset.installmentProviderOpen;
        const primaryModal = document.getElementById(primaryModalId);
        if (!primaryModal) return;

        const primaryOptions = primaryModal.querySelector('.installment-plans__provider-options');
        const seen = new Set();

        if (primaryOptions) {
          primaryOptions.querySelectorAll('[data-installment-option]').forEach((option) => {
            const key = `${option.dataset.months || ''}|${option.dataset.fee || ''}`;
            seen.add(key);
          });
        }

        groupButtons.slice(1).forEach((button) => {
          const modalId = button.dataset.installmentProviderOpen;
          const modal = document.getElementById(modalId);

          if (modal && primaryOptions) {
            modal.querySelectorAll('[data-installment-option]').forEach((option) => {
              const key = `${option.dataset.months || ''}|${option.dataset.fee || ''}`;
              if (!seen.has(key)) {
                seen.add(key);
                primaryOptions.appendChild(option.cloneNode(true));
              }
            });

            const primaryLimits = primaryModal.querySelector('.installment-plans__provider-limits');
            const secondaryLimits = modal.querySelector('.installment-plans__provider-limits');
            if (!primaryLimits && secondaryLimits) {
              primaryModal.querySelector('.installment-plans__provider-body')?.appendChild(secondaryLimits.cloneNode(true));
            }
          }

          button.remove();
          modal?.remove();
        });

        if (primaryOptions) {
          const options = Array.from(primaryOptions.querySelectorAll('[data-installment-option]'));
          options
            .sort((a, b) => Number(a.dataset.months || 0) - Number(b.dataset.months || 0))
            .forEach((option) => primaryOptions.appendChild(option));
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.boundVariantChange);
      document.removeEventListener('product:variant-change', this.boundVariantChange);
      this.priceObserver?.disconnect();
      if (this.syncTimer) window.clearTimeout(this.syncTimer);
    }

    toggleDropdown() {
      if (!this.dropdown) return;
      this.dropdown.open = !this.dropdown.open;
      this.toggleButton?.setAttribute('aria-expanded', String(this.dropdown.open));
      if (this.dropdown.open) this.dropdown.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    openProviderModal(id) {
      const modal = document.getElementById(id);
      if (!modal) return;
      modal.hidden = false;
      document.body.classList.add('installment-modal-open');
      modal.querySelector('.installment-plans__close')?.focus();
    }

    closeProviderModal(modal) {
      if (!modal) return;
      modal.hidden = true;
      if (!document.querySelector('[data-installment-provider-modal]:not([hidden])')) document.body.classList.remove('installment-modal-open');
    }

    closeAllModals() {
      this.querySelectorAll('[data-installment-provider-modal]:not([hidden])').forEach((modal) => { modal.hidden = true; });
      document.body.classList.remove('installment-modal-open');
    }

    handleVariantChange(event) {
      const variant = event.detail?.variant;
      if (variant && typeof variant.price === 'number') {
        this.baseVariantPrice = variant.price;
        this.dataset.currentPrice = variant.price;
        this.lastAppliedPrice = variant.price;
        this.updateOptions(variant.price);
      }
      [50, 250, 550, 900, 1500, 2500].forEach((delay) => window.setTimeout(() => this.syncFromRenderedPrice(), delay));
    }

    observeExternalDiscountPrice() {
      if (!this.productRoot || typeof MutationObserver === 'undefined') return;
      this.priceObserver = new MutationObserver((mutations) => {
        const external = mutations.some((mutation) => {
          const target = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
          return target && !target.closest('installment-plans');
        });
        if (!external) return;
        window.clearTimeout(this.syncTimer);
        this.syncTimer = window.setTimeout(() => this.syncFromRenderedPrice(), 60);
      });
      this.priceObserver.observe(this.productRoot, { subtree:true, childList:true, characterData:true, attributes:true, attributeFilter:['class','style','hidden','data-price','data-discounted-price','data-sale-price'] });
    }

    syncFromRenderedPrice() {
      const detected = this.detectCurrentCustomerPrice();
      if (!detected || detected <= 0) return;
      if (Math.abs(Number(this.lastAppliedPrice || 0) - detected) < 1) return;
      this.lastAppliedPrice = detected;
      this.dataset.currentPrice = detected;
      this.updateOptions(detected);
    }

    detectCurrentCustomerPrice() {
      if (!this.productRoot) return Number(this.dataset.currentPrice || 0);
      const base = Number(this.baseVariantPrice || this.dataset.currentPrice || 0);
      const candidates = [];
      const selectors = [
        '[class*="discount-mixer" i]','[id*="discount-mixer" i]','[class*="discountmixer" i]','[id*="discountmixer" i]',
        '[class*="dxmixer" i]','[id*="dxmixer" i]','[data-discount-mixer-price]','[data-dxmixer-price]',
        '[data-discounted-price]','[data-sale-price]','[class*="discounted-price" i]','[class*="sale-price" i]',
        '.js-product-price .price__current','.product-info__price .price__current','.price--on-sale .price__current','[data-product-price]','.price__current'
      ];
      selectors.forEach((selector) => {
        let nodes=[]; try { nodes=this.productRoot.querySelectorAll(selector); } catch(e) { return; }
        nodes.forEach((node) => {
          if (node.closest('installment-plans') || !this.isVisible(node)) return;
          ['data-discount-mixer-price','data-dxmixer-price','data-discounted-price','data-sale-price','data-price'].forEach((attr) => {
            if (!node.hasAttribute?.(attr)) return;
            this.extractPrices(node.getAttribute(attr) || '').forEach((value) => this.pushCandidate(candidates,value,base));
          });
          this.extractPrices(node.textContent || '').forEach((value) => this.pushCandidate(candidates,value,base));
        });
      });
      return candidates.length ? Math.min(...candidates) : base;
    }

    pushCandidate(output,value,base){if(!value||value<=0)return;let cents=value;if(base>0&&value>base*5&&value/100<=base*1.1)cents=Math.round(value/100);if(base>0&&cents<base*.05)return;if(base>0&&cents>base*1.02)return;output.push(Math.round(cents));}
    isVisible(node){if(!(node instanceof Element))return false;const s=window.getComputedStyle(node);return s.display!=='none'&&s.visibility!=='hidden'&&s.opacity!=='0'&&node.getClientRects().length>0;}
    extractPrices(text){const source=String(text||'').replace(/\u00a0/g,' ');const matches=source.match(/(?:LKR\s*|Rs\.?\s*|₨\s*)?\d[\d,]*(?:\.\d{1,2})?/gi)||[];const out=[];matches.forEach((m)=>{const c=/LKR|Rs\.?|₨/i.test(m);const n=Number(m.replace(/LKR|Rs\.?|₨/gi,'').replace(/,/g,'').trim());if(!Number.isFinite(n)||n<=0)return;if(!c&&n<100)return;out.push(Math.round(n*100));});return out;}

    updateOptions(priceInCents) {
      this.querySelectorAll('[data-installment-option]').forEach((option) => {
        const months=Number(option.dataset.months||0), fee=Number(option.dataset.fee||0), min=Number(option.dataset.minCents||0), max=Number(option.dataset.maxCents||0);
        if(!months)return;
        const monthly=Math.round((priceInCents*((100+fee)/100))/months);
        const amount=option.querySelector('[data-installment-option-amount]');
        if(amount) amount.textContent=this.formatMoney(monthly);
        const eligible=(!min||priceInCents>=min)&&(!max||priceInCents<=max);
        option.classList.toggle('is-ineligible',!eligible);
        const message=option.querySelector('[data-installment-option-ineligible]');
        if(message){message.hidden=eligible;if(!eligible){if(min&&priceInCents<min)message.textContent=`Available from ${this.formatMoney(min)}`;else if(max&&priceInCents>max)message.textContent=`Available up to ${this.formatMoney(max)}`;else message.textContent='Not available for this price';}}
      });
    }

    formatMoney(cents){try{return new Intl.NumberFormat(document.documentElement.lang||'en-LK',{style:'currency',currency:'LKR',minimumFractionDigits:0,maximumFractionDigits:2}).format(cents/100).replace('LKR','Rs');}catch(e){return `Rs ${(cents/100).toLocaleString(undefined,{maximumFractionDigits:2})}`;}}
  }
  customElements.define('installment-plans', InstallmentPlans);
}
